import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Activity, Thermometer, Upload, FileSpreadsheet, Download, Package,
  CheckCircle, AlertCircle, X, Battery, BatteryLow, BatteryWarning,
  BatteryFull, BatteryMedium, Wifi, WifiOff, Wrench, AlertTriangle,
  Signal, MapPin, Clock, ChevronDown, ChevronUp, MoreVertical, Building2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BatteryIcon = ({ level }) => {
  if (level == null) return <Battery className="w-4 h-4 text-slate-400" />;
  if (level < 10) return <BatteryLow className="w-4 h-4 text-red-500" />;
  if (level < 20) return <BatteryWarning className="w-4 h-4 text-amber-500" />;
  if (level < 60) return <BatteryMedium className="w-4 h-4 text-yellow-500" />;
  return <BatteryFull className="w-4 h-4 text-emerald-500" />;
};

const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  damaged: { label: 'Damaged', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  maintenance: { label: 'Maintenance', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  offline: { label: 'Offline', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

const Devices = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [assignDevice, setAssignDevice] = useState(null);
  const [assignReceiverId, setAssignReceiverId] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({ device_id: '', name: '', type: 'temperature_sensor' });
  const queryClient = useQueryClient();

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => (await axios.get(`${API}/devices`)).data,
    refetchInterval: 10000,
  });

  const { data: shipments = [] } = useQuery({
    queryKey: ['shipments'],
    queryFn: async () => (await axios.get(`${API}/shipments`)).data,
    refetchInterval: 10000,
  });

  const { data: fleetHealth } = useQuery({
    queryKey: ['fleet-health'],
    queryFn: async () => (await axios.get(`${API}/devices/fleet/health`)).data,
    refetchInterval: 15000,
  });

  const { data: receiverOrgs = [] } = useQuery({
    queryKey: ['receiver-orgs'],
    queryFn: async () => {
      const res = await axios.get(`${API}/organizations`);
      return res.data.filter(o => o.type === 'receiver');
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ deviceId, receiverId }) => axios.patch(`${API}/devices/${deviceId}`, { assigned_receiver_id: receiverId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setIsAssignDialogOpen(false);
      setAssignDevice(null);
      setAssignReceiverId('');
      toast.success('Device assigned to receiver');
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Assignment failed'),
  });

  const { data: telemetryData = [] } = useQuery({
    queryKey: ['device-telemetry', selectedDevice],
    queryFn: async () => {
      if (!selectedDevice) return [];
      return (await axios.get(`${API}/telemetry/${selectedDevice}?hours=1`)).data;
    },
    enabled: !!selectedDevice,
    refetchInterval: 5000,
  });

  const createMutation = useMutation({
    mutationFn: (data) => axios.post(`${API}/devices`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setIsDialogOpen(false);
      setFormData({ device_id: '', name: '', type: 'temperature_sensor' });
      toast.success('Device added successfully');
    },
    onError: (e) => toast.error(`Failed: ${e.message}`),
  });

  const statusMutation = useMutation({
    mutationFn: ({ deviceId, status }) => axios.patch(`${API}/devices/${deviceId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-health'] });
      toast.success('Device status updated');
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Update failed'),
  });

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return (await axios.post(`${API}/devices/import-csv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setImportResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} devices`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Import failed'),
  });

  const filteredDevices = statusFilter === 'all'
    ? devices
    : devices.filter(d => d.status === statusFilter);

  const fh = fleetHealth || {};

  const chartData = React.useMemo(() =>
    telemetryData.slice(-30).reverse().map(t => ({
      time: new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      temperature: t.temperature,
    })), [telemetryData]);

  const latestTelemetry = telemetryData[0];
  const selectedDeviceData = devices.find(d => d.device_id === selectedDevice);

  const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="devices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" data-testid="devices-page-title">Pallet Pool Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">Device management and fleet health monitoring</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsImportDialogOpen(true)} data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" />Import CSV
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-device-btn"><Plus className="w-4 h-4 mr-2" />Add Device</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle>Add New Device</DialogTitle>
                <DialogDescription>Register a new IoT tracker</DialogDescription>
              </DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4" data-testid="device-form">
                <div className="space-y-2">
                  <Label>Device ID</Label>
                  <Input data-testid="device-id-input" placeholder="PALLET_004" value={formData.device_id} onChange={e => setFormData(p => ({ ...p, device_id: e.target.value }))} required />
                </div>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input data-testid="device-name-input" placeholder="Cold Storage Pallet 4" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} required />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" type="button" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="submit-device-btn">{createMutation.isPending ? 'Adding...' : 'Add Device'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Fleet Health Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="fleet-health-grid">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold">{fh.total ?? devices.length}</p>
            <p className="text-[11px] text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="border-l-2 border-l-emerald-500">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{fh.active ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className="border-l-2 border-l-red-500">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-red-600">{fh.damaged ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Damaged</p>
          </CardContent>
        </Card>
        <Card className="border-l-2 border-l-amber-500">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{fh.maintenance ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Maintenance</p>
          </CardContent>
        </Card>
        <Card className="border-l-2 border-l-slate-300">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-slate-500">{fh.offline ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Offline</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold">{fh.avg_battery ?? '—'}%</p>
            <p className="text-[11px] text-muted-foreground">Avg Battery</p>
          </CardContent>
        </Card>
        <Card className="border-l-2 border-l-amber-400">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{fh.low_battery ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Low Battery</p>
          </CardContent>
        </Card>
        <Card className="border-l-2 border-l-red-400">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-red-600">{fh.critical_battery ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Critical</p>
          </CardContent>
        </Card>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2" data-testid="status-filter">
        {['all', 'active', 'damaged', 'maintenance'].map(s => (
          <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" className="text-xs capitalize" onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}>
            {s === 'all' ? `All (${devices.length})` : `${s} (${devices.filter(d => d.status === s).length})`}
          </Button>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Devices ({filteredDevices.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1">
              {isLoading ? <p className="text-sm text-muted-foreground text-center py-4">Loading...</p> :
               filteredDevices.length === 0 ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No devices</p>
                </div>
              ) : filteredDevices.map(device => {
                const sc = statusConfig[device.status] || statusConfig.offline;
                const isExpanded = expandedId === device.device_id;
                return (
                  <div key={device.device_id} className={`rounded-lg border transition-all ${selectedDevice === device.device_id ? 'ring-2 ring-blue-400 border-blue-200' : 'border-border hover:border-slate-300'}`}>
                    <div className="flex items-center gap-2 p-2.5 cursor-pointer" onClick={() => setSelectedDevice(device.device_id)} data-testid={`device-item-${device.device_id}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{device.name}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{device.device_id}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <BatteryIcon level={device.battery_level} />
                        <span className="text-[10px] text-muted-foreground w-7 text-right">
                          {device.battery_level != null ? `${Math.round(device.battery_level)}%` : '—'}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => e.stopPropagation()} data-testid={`device-menu-${device.device_id}`}>
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-36">
                            <DropdownMenuItem onClick={() => statusMutation.mutate({ deviceId: device.device_id, status: 'active' })} data-testid={`set-active-${device.device_id}`}>
                              <CheckCircle className="w-3.5 h-3.5 mr-2 text-emerald-500" />Set Active
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => statusMutation.mutate({ deviceId: device.device_id, status: 'damaged' })} data-testid={`set-damaged-${device.device_id}`}>
                              <AlertTriangle className="w-3.5 h-3.5 mr-2 text-red-500" />Mark Damaged
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => statusMutation.mutate({ deviceId: device.device_id, status: 'maintenance' })} data-testid={`set-maintenance-${device.device_id}`}>
                              <Wrench className="w-3.5 h-3.5 mr-2 text-amber-500" />Maintenance
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setAssignDevice(device); setAssignReceiverId(device.assigned_receiver_id || ''); setIsAssignDialogOpen(true); }} data-testid={`assign-receiver-${device.device_id}`}>
                              <Building2 className="w-3.5 h-3.5 mr-2 text-purple-500" />Assign Receiver
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); setExpandedId(isExpanded ? null : device.device_id); }}>
                          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t grid grid-cols-2 gap-y-1.5 text-[11px]">
                        <div className="flex items-center gap-1"><Badge className={`${sc.color} text-[10px] px-1.5 h-5`}>{sc.label}</Badge></div>
                        <div className="flex items-center gap-1 justify-end"><Clock className="h-3 w-3 text-muted-foreground" />{formatTime(device.last_seen)}</div>
                        <div className="flex items-center gap-1"><Thermometer className="h-3 w-3 text-blue-500" />{device.last_temperature != null ? `${device.last_temperature}°C` : '—'}</div>
                        <div className="flex items-center gap-1 justify-end"><MapPin className="h-3 w-3 text-red-400" />{device.last_latitude ? `${device.last_latitude.toFixed(3)}, ${device.last_longitude?.toFixed(3)}` : '—'}</div>
                        <div className="flex items-center gap-1"><Signal className="h-3 w-3 text-slate-500" />{device.signal_strength ?? '—'} dBm</div>
                        {device.grai && (
                          <div className="col-span-2 mt-1 p-1.5 bg-blue-50 rounded border border-blue-100">
                            <div className="flex items-center gap-1 text-[10px] text-blue-700 font-semibold">
                              <Package className="w-3 h-3" /> GRAI: <span className="font-mono">{device.grai}</span>
                            </div>
                            {(() => {
                              const shipment = shipments.find(s => s.device_id === device.device_id && ['activated', 'in_transit'].includes(s.status));
                              return shipment ? (
                                <div className="text-[9px] text-muted-foreground mt-0.5 truncate">{shipment.origin} → {shipment.destination}</div>
                              ) : null;
                            })()}
                          </div>
                        )}
                        {device.assigned_receiver_name && (
                          <div className="col-span-2 mt-1 p-1.5 bg-purple-50 rounded border border-purple-100">
                            <div className="flex items-center gap-1 text-[10px] text-purple-700 font-semibold">
                              <Building2 className="w-3 h-3" /> Receiver: {device.assigned_receiver_name}
                            </div>
                          </div>
                        )}
                        {device.firmware_version && <div className="col-span-2 text-muted-foreground">FW: {device.firmware_version}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Device Details */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedDevice ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <Activity className="w-14 h-14 text-slate-200 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-1">Select a device</h3>
                <p className="text-sm text-muted-foreground">Click a device to view readings and history</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Device header */}
              {selectedDeviceData && (
                <Card data-testid="device-detail-header">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{selectedDeviceData.name}</h3>
                        <p className="text-xs text-muted-foreground font-mono">{selectedDeviceData.device_id}</p>
                      </div>
                      <Badge className={statusConfig[selectedDeviceData.status]?.color || 'bg-slate-100'}>
                        {statusConfig[selectedDeviceData.status]?.label || selectedDeviceData.status}
                      </Badge>
                    </div>
                    {selectedDeviceData.grai && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="device-grai-info">
                        <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">GRAI (Returnable Asset ID)</div>
                        <div className="font-mono font-semibold text-sm mt-1">{selectedDeviceData.grai}</div>
                        {(() => {
                          const shipment = shipments.find(s => s.device_id === selectedDeviceData.device_id && ['activated', 'in_transit'].includes(s.status));
                          return shipment ? (
                            <div className="text-xs text-slate-600 mt-2 pt-2 border-t border-blue-200">
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500">Active shipment</span>
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">{shipment.status}</Badge>
                              </div>
                              <div className="mt-1">{shipment.origin} → {shipment.destination}</div>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Readings cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card data-testid="current-temperature">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-2 mb-1"><Thermometer className="w-4 h-4 text-blue-500" /><span className="text-xs text-muted-foreground">Temperature</span></div>
                    <p className="text-2xl font-bold">{latestTelemetry?.temperature?.toFixed(1) ?? '—'}°C</p>
                  </CardContent>
                </Card>
                <Card data-testid="current-battery">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-2 mb-1"><BatteryIcon level={selectedDeviceData?.battery_level} /><span className="text-xs text-muted-foreground">Battery</span></div>
                    <p className="text-2xl font-bold">{selectedDeviceData?.battery_level != null ? `${Math.round(selectedDeviceData.battery_level)}%` : '—'}</p>
                    {selectedDeviceData?.battery_level != null && <Progress value={selectedDeviceData.battery_level} className="mt-2 h-1.5" />}
                  </CardContent>
                </Card>
                <Card data-testid="current-signal">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-2 mb-1"><Signal className="w-4 h-4 text-slate-500" /><span className="text-xs text-muted-foreground">Signal</span></div>
                    <p className="text-2xl font-bold">{selectedDeviceData?.signal_strength ?? '—'}<span className="text-sm font-normal ml-1">dBm</span></p>
                  </CardContent>
                </Card>
                <Card data-testid="current-location">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-red-400" /><span className="text-xs text-muted-foreground">Location</span></div>
                    <p className="text-sm font-medium">{selectedDeviceData?.last_latitude ? `${selectedDeviceData.last_latitude.toFixed(4)}, ${selectedDeviceData.last_longitude?.toFixed(4)}` : 'No GPS'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Chart */}
              <Card data-testid="device-temperature-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Temperature History</CardTitle>
                  <CardDescription className="text-xs">Last hour of readings</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    {chartData.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No data available</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                          <Line type="monotone" dataKey="temperature" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 2 }} name="Temp (°C)" />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* CSV Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(v) => { setIsImportDialogOpen(v); if (!v) setImportResult(null); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-green-600" />Import from CSV</DialogTitle>
            <DialogDescription>Bulk import pallets/devices</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted/50 rounded-lg text-xs">
              <p className="font-medium mb-1">Required columns:</p>
              <code className="block bg-background p-2 rounded text-[11px] font-mono">device_id,name,type</code>
              <Button variant="link" size="sm" className="mt-1 h-auto p-0 text-xs" onClick={() => {
                const csv = 'device_id,name,type\nPALLET_001,Cold Pallet 1,temperature_sensor';
                const b = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'template.csv'; a.click();
              }}><Download className="w-3 h-3 mr-1" />Download template</Button>
            </div>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${importMutation.isPending ? 'border-blue-300 bg-blue-50' : 'border-border hover:border-blue-400'}`}>
              <input ref={fileInputRef} type="file" accept=".csv" onChange={e => { const f = e.target.files?.[0]; if (f) { setImportResult(null); importMutation.mutate(f); } }} className="hidden" data-testid="csv-file-input" />
              {importMutation.isPending ? (
                <div className="animate-spin w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full mx-auto" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>Browse Files</Button>
                </>
              )}
            </div>
            {importResult && (
              <div className={`p-3 rounded-lg border text-sm ${importResult.imported > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex gap-4">
                  <div><span className="text-xl font-bold text-emerald-600">{importResult.imported}</span><span className="text-xs text-muted-foreground ml-1">imported</span></div>
                  <div><span className="text-xl font-bold text-amber-600">{importResult.skipped}</span><span className="text-xs text-muted-foreground ml-1">skipped</span></div>
                </div>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-red-600 max-h-16 overflow-y-auto">{importResult.errors.map((e) => <li key={e}>{e}</li>)}</ul>
                )}
              </div>
            )}
            <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => { setIsImportDialogOpen(false); setImportResult(null); }}>Close</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Receiver Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="assign-receiver-dialog">
          <DialogHeader>
            <DialogTitle>Assign to Receiver</DialogTitle>
            <DialogDescription>Link device <span className="font-mono font-bold">{assignDevice?.name || assignDevice?.device_id}</span> to a receiver company</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Receiver Company</Label>
              <Select value={assignReceiverId} onValueChange={setAssignReceiverId}>
                <SelectTrigger data-testid="assign-receiver-select">
                  <SelectValue placeholder="Select a receiver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No receiver —</SelectItem>
                  {receiverOrgs.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      <span className="flex items-center gap-2"><Building2 className="w-3 h-3" /> {o.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => assignMutation.mutate({ deviceId: assignDevice?.device_id, receiverId: assignReceiverId === 'none' ? null : assignReceiverId })}
              disabled={assignMutation.isPending}
              data-testid="assign-receiver-submit"
            >
              {assignMutation.isPending ? 'Saving...' : 'Assign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Devices;
